import React, {useEffect, useState} from "react";
import {Button, Col, Form, Modal, Row} from "react-bootstrap";
import InputMask from "react-input-mask";
import {useAlert} from "react-alert";
import {Loading} from "../../../../genericComponents";
import MoneyField from "../../../../components/fields/MoneyField";
import {generateDocument, getDocumentTemplates, getGenerationData} from "../../../../services/RequestService";
import {fn, masks} from "../../../../utils";
import location from "../../../../utils/location";
import CboAutoComplete from "../../../../components/InputAutoComplete/CboAutoComplete";
import "./ModalGeradorDocumento.css";

const EMAIL_REGEX = /^\S+@\S+\.\S+$/;

export default function ModalGeradorDocumento({ onHide, show, process }) {
  const processId = process.id;
  const alert = useAlert();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [fields, setFields] = useState([]);

  useEffect(() => {
    getDocumentTemplates().then(({ ok, data }) => {
      if (ok) setTemplates(data);
    });
  }, []);

  async function selectTemplate(id) {
    setTemplateId(id);
    setFields([]);
    if (!id) return;
    setLoading(true);
    const { ok, data } = await getGenerationData(processId, id);
    if (ok) {
      setFields(data.fields || []);
    } else {
      alert.show(data?.log || "Erro ao carregar o modelo", { type: "error" });
    }
    setLoading(false);
  }

  const setFieldValue = (index, value) =>
    setFields((fields) => fields.map((f, i) => (i === index ? { ...f, value } : f)));

  function renderInput(f, i) {
    const value = f.value || "";
    switch (f.type) {
      case "CPF":
        return (
          <Form.Control
            value={value}
            placeholder="000.000.000-00"
            onChange={(e) => setFieldValue(i, masks.CPF(e.target.value))}
          />
        );
      case "PHONE":
        return (
          <Form.Control
            value={value}
            placeholder="(00) 00000-0000"
            onChange={(e) => setFieldValue(i, masks.inputMaskTELWithDDD(e.target.value))}
          />
        );
      case "DATE":
        return (
          <InputMask
            mask="99/99/9999"
            className="form-control"
            placeholder="dd/mm/aaaa"
            value={value}
            onChange={(e) => setFieldValue(i, e.target.value)}
          />
        );
      case "MONEY":
        return (
          <MoneyField
            className="form-control"
            value={value}
            onChange={(e) => setFieldValue(i, e.target.value)}
          />
        );
      case "EMAIL":
        return (
          <Form.Control
            type="email"
            value={value}
            placeholder="email@exemplo.com"
            className={value === "" || EMAIL_REGEX.test(value) ? "" : "is-invalid"}
            onChange={(e) => setFieldValue(i, e.target.value)}
          />
        );
      case "BOOLEAN": {
        // valores salvos voltam formatados ([x]/[ ]); mapeia de volta para o select
        const boolValue = value === "[x]" ? "Sim" : value === "[ ]" ? "Não" : value;
        return (
          <Form.Control
            as="select"
            value={boolValue}
            onChange={(e) => setFieldValue(i, e.target.value)}
          >
            <option value="">Selecione...</option>
            <option value="Sim">Sim</option>
            <option value="Não">Não</option>
          </Form.Control>
        );
      }
      case "UF":
        return (
          <Form.Control
            as="select"
            value={value}
            onChange={(e) => setFieldValue(i, e.target.value)}
          >
            <option value="">Selecione...</option>
            {location.states.map((uf) => (
              <option key={uf.value} value={uf.value}>
                {uf.label}
              </option>
            ))}
          </Form.Control>
        );
      case "CURRENT_DATE":
        return (
          <Form.Control
            value={value}
            placeholder="ex.: 29 de Agosto de 2026"
            onChange={(e) => setFieldValue(i, e.target.value)}
          />
        );
      case "CBO":
        // guarda a descrição (é o que vai no documento); busca na tabela cbo e
        // permite digitar/adicionar uma profissão que não esteja na lista
        return (
          <CboAutoComplete
            value={value || null}
            onChange={(descricao) => setFieldValue(i, descricao || "")}
          />
        );
      default:
        return <Form.Control value={value} onChange={(e) => setFieldValue(i, e.target.value)} />;
    }
  }

  async function generate() {
    setLoading(true);
    const values = {};
    fields.forEach((f) => {
      values[f.variable] = f.value || "";
    });
    const { ok, data } = await generateDocument({ processId, templateId: parseInt(templateId), values });
    if (ok) {
      fn.downloadBase64File(data.base64, data.fileName, fn.DOCX_MIME);
      alert.show("Documento gerado com sucesso. Os valores foram salvos no processo", { type: "success" });
    } else {
      alert.show(data?.log || "Erro ao gerar documento", { type: "error" });
    }
    setLoading(false);
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static" scrollable dialogClassName="doc-generator-fill-modal">
      <Modal.Header closeButton>
        <Modal.Title>Gerador de Documentos</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Loading loading={loading}>
          <Form.Group>
            <Form.Label>Modelo</Form.Label>
            <Form.Control as="select" value={templateId} onChange={(e) => selectTemplate(e.target.value)}>
              <option value="">Selecione um modelo</option>
              {(templates || []).map((el) => (
                <option key={el.value} value={el.value}>
                  {el.label}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          {fields.length > 0 && (
            <Row>
              {fields.map((f, i) => (
                <Col md={6} key={f.variable}>
                  <Form.Group>
                    <Form.Label>{f.description || f.variable}</Form.Label>
                    {renderInput(f, i)}
                  </Form.Group>
                </Col>
              ))}
            </Row>
          )}
          {templateId !== "" && fields.length === 0 && !loading && (
            <div className="text-muted">Este modelo não possui variáveis cadastradas.</div>
          )}
        </Loading>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="success" disabled={!templateId || loading} onClick={generate}>
          <i className="fas fa-file-word mr-1" />
          Gerar Documento
        </Button>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
